import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { buildUrl } from 'utils';

import Box from '@mui/material/Box';
import { BaseModal } from 'components/UI';
import { withFormik, Field } from 'formik';
import Button from '@mui/material/Button';
import { TextField } from 'components/FormFields';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { DocumentTypesSelect } from 'components/AsyncFields';

const DocumentModal = ({
  open,
  handleClose,
  errors,
  mode,
  submitForm,
  asyncErrors,
  setErrors,
  setFieldTouched,
  document_type_name,
}) => {
  const { isCreatingDocument } = useSelector((state) => state.documents);
  useDeepCompareEffect(() => {
    if (asyncErrors) {
      setErrors(asyncErrors);
      Object.keys(asyncErrors).map((field) => setFieldTouched(field, true, false));
    }
  }, [asyncErrors, setErrors, setFieldTouched]);
  return (
    <BaseModal
      size="sm"
      open={open}
      hasFooter={false}
      hasCancelButton={true}
      enableCloseButton={false}
      aria-labelledby="modal-title"
      title={
        mode === 'create'
          ? i18n.t('documents.create_new_document')
          : i18n.t('documents.edit_document')
      }
    >
      <Box width="100%" mt={2}>
        <Field
          margin="none"
          name="document_type_id"
          id="documentTypesSelect"
          component={DocumentTypesSelect}
          customparams={{ keyword: document_type_name }}
          asyncErrors={errors?.document_type_id}
          label={i18n.t('documents.document_type')}
        />
      </Box>
      <Button
        sx={{ textTransform: 'inherit', my: 1 }}
        startIcon={<AddCircleIcon />}
        data-cy="add-document-type"
        href={buildUrl('/document-types')}
        target="_blank"
        color="primary"
      >
        {i18n.t('documents.add_document_type')}
      </Button>
      <Box width="100%">
        <Field
          type="text"
          margin="none"
          required={false}
          name="description"
          inputProperties={{}}
          component={TextField}
          asyncErrors={errors?.description}
          label={i18n.t('common.description')}
        />
      </Box>
      <Box mt={5} width="100%" role="group" display="flex" justifyContent="flex-end">
        <Button
          sx={{ letterSpacing: 0.7, fontWeight: 500, ml: 1, display: 'inline' }}
          color="primary"
          onClick={handleClose}
          disabled={isCreatingDocument}
        >
          {i18n.t('documents.cancel')}
        </Button>
        <Button
          sx={{ letterSpacing: 0.7, fontWeight: 500, ml: 1, display: 'inline' }}
          type="submit"
          color="primary"
          onClick={submitForm}
          disabled={isCreatingDocument}
        >
          {mode === 'create' ? i18n.t('documents.create') : i18n.t('common.save')}
        </Button>
      </Box>
    </BaseModal>
  );
};

DocumentModal.propTypes = {
  open: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'update']),
  handleClose: PropTypes.func,
  handleCreate: PropTypes.func,
  document_type_id: PropTypes.number,
  description: PropTypes.string,
  errors: PropTypes.object,
  submitForm: PropTypes.func,
  asyncErrors: PropTypes.object,
  setErrors: PropTypes.func,
  setFieldTouched: PropTypes.func,
  document_type_name: PropTypes.string,
};

DocumentModal.defaultProps = {
  open: true,
  handleClose: undefined,
  handleCreate: undefined,
  name: undefined,
  mode: 'create',
  asyncErrors: {},
};

export default withFormik({
  mapPropsToValues: (props) => ({
    document_type_id: props.document_type_id,
    description: props.description,
  }),
  displayName: 'DocumentModalForm',
  validationSchema: () => {
    const DocumentSchema = Yup.object().shape({
      document_type_id: Yup.string().required(i18n.t('common.required_field')).nullable(),
      description: Yup.string().nullable(),
    });
    return DocumentSchema;
  },
  handleSubmit: (values, { props: { handleCreate } }) =>
    handleCreate(values.document_type_id, values?.description),
})(DocumentModal);
